import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAllGamicsAnalytics } from '@/store/Gamic';
import { fetchAllCampaignsAnalytics } from '@/store/Campaign';
import { dayNames, monthNames } from '@/utils/Names';
import {
  RawSummaryData,
  SummaryData,
  TimeRange,
  ChangeDirection,
  SummaryItem,
} from '../constants/types';

export const useSummaryData = () => {
  const [timeRangeGamic, setTimeRangeGamic] = useState<TimeRange>('yearly');
  const [timeRangeCampaign, setTimeRangeCampaign] =
    useState<TimeRange>('yearly');
  const [rawGamicSummaryData, setRawGamicSummaryData] =
    useState<RawSummaryData>({ previous: [], current: [] });
  const [rawCampaignSummaryData, setRawCampaignSummaryData] =
    useState<RawSummaryData>({ previous: [], current: [] });

  const dispatch = useDispatch<any>();

  useEffect(() => {
    const fetchSummaryData = async () => {
      const timeRange = timeRangeCampaign;
      const res = await dispatch(fetchAllCampaignsAnalytics({ timeRange }));

      const raw = res.payload.data;
      const half = raw.length / 2;
      setRawCampaignSummaryData({
        previous: raw.slice(0, half),
        current: raw.slice(half),
      });
    };
    fetchSummaryData();
  }, [dispatch, timeRangeCampaign]);

  useEffect(() => {
    const fetchSummaryData = async () => {
      const timeRange = timeRangeGamic;
      const res = await dispatch(fetchAllGamicsAnalytics({ timeRange }));

      const raw = res.payload.data;
      const half = raw.length / 2;
      setRawGamicSummaryData({
        previous: raw.slice(0, half),
        current: raw.slice(half),
      });
    };
    fetchSummaryData();
  }, [dispatch, timeRangeGamic]);

  const summaryDataCampaign: SummaryData[] = useMemo(() => {
    const prevArr = rawCampaignSummaryData.previous;
    const currArr = rawCampaignSummaryData.current;
    if (prevArr.length === 0 || currArr.length === 0) return [];

    const keys = ['total', 'active', 'approved', 'pending', 'rejected'] as const;
    type DataKey = (typeof keys)[number];

    const labelsMap: Record<DataKey, string> = {
      total: 'Total Campaigns',
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      active: 'Currently Live',
    };

    const sumByKey = (arr: SummaryItem[], key: DataKey) =>
      arr.reduce((acc, item) => acc + Number(item[key]), 0);

    return keys.map((key) => {
      const prev = sumByKey(prevArr, key);
      const curr = sumByKey(currArr, key);

      let direction: ChangeDirection = 'neutral';
      let pct = 0;

      if (curr > prev) {
        direction = 'up';
        pct = prev === 0 ? 100 : ((curr - prev) / prev) * 100;
      } else if (curr < prev) {
        direction = 'down';
        pct = prev === 0 ? 0 : ((prev - curr) / prev) * 100;
      }

      const pctRounded = Number(pct.toFixed(2));
      const pctString =
        direction === 'up'
          ? `+${pctRounded}%`
          : direction === 'down'
            ? `-${pctRounded}%`
            : `0%`;

      return {
        label: labelsMap[key],
        value: curr,
        pct: pctRounded,
        pctString,
        direction,
      };
    });
  }, [rawCampaignSummaryData]);

  const chartDataCampaign = useMemo(() => {
    const data = rawCampaignSummaryData.current;
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      const d = new Date(item.period);
      switch (timeRangeCampaign) {
        case 'yearly':
          return { label: monthNames[d.getMonth()], value: Number(item.total) };
        case 'monthly':
          return { label: d.getDate().toString(), value: Number(item.total) };
        case 'weekly':
          return { label: dayNames[d.getDay()], value: Number(item.total) };
        case 'daily':
          return { label: `${d.getHours()}`, value: Number(item.total) };
        default:
          return { label: item.period, value: Number(item.total) };
      }
    });
  }, [rawCampaignSummaryData]);

  const summaryDataGamic: SummaryData[] = useMemo(() => {
    const prevArr = rawGamicSummaryData.previous;
    const currArr = rawGamicSummaryData.current;
    if (prevArr.length === 0 || currArr.length === 0) return [];

    const keys = ['total', 'approved', 'pending', 'rejected'] as const;
    type DataKey = (typeof keys)[number];

    const labelsMap: Record<DataKey, string> = {
      total: 'Total Gamics',
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
    };

    const sumByKey = (arr: SummaryItem[], key: DataKey) =>
      arr.reduce((acc, item) => acc + Number(item[key]), 0);

    return keys.map((key) => {
      const prev = sumByKey(prevArr, key);
      const curr = sumByKey(currArr, key);

      let direction: ChangeDirection = 'neutral';
      let pct = 0;

      if (curr > prev) {
        direction = 'up';
        pct = prev === 0 ? 100 : ((curr - prev) / prev) * 100;
      } else if (curr < prev) {
        direction = 'down';
        pct = prev === 0 ? 0 : ((prev - curr) / prev) * 100;
      }

      const pctRounded = Number(pct.toFixed(2));
      const pctString =
        direction === 'up'
          ? `+${pctRounded}%`
          : direction === 'down'
            ? `-${pctRounded}%`
            : `0%`;

      return {
        label: labelsMap[key],
        value: curr,
        pct: pctRounded,
        pctString,
        direction,
      };
    });
  }, [rawGamicSummaryData]);

  const chartDataGamic = useMemo(() => {
    const data = rawGamicSummaryData.current;
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      const d = new Date(item.period);
      switch (timeRangeGamic) {
        case 'yearly':
          return { label: monthNames[d.getMonth()], value: Number(item.total) };
        case 'monthly':
          return { label: d.getDate().toString(), value: Number(item.total) };
        case 'weekly':
          return { label: dayNames[d.getDay()], value: Number(item.total) };
        case 'daily':
          return { label: `${d.getHours()}`, value: Number(item.total) };
        default:
          return { label: item.period, value: Number(item.total) };
      }
    });
  }, [rawGamicSummaryData]);

  return {
    summaryDataGamic,
    summaryDataCampaign,
    chartDataGamic,
    chartDataCampaign,
    timeRangeGamic,
    setTimeRangeGamic,
    timeRangeCampaign,
    setTimeRangeCampaign,
  };
};
